import { RotateCcw, Car } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScoreGauge } from "@/components/ScoreGauge";
import { DisclosureList } from "@/components/DisclosureList";
import { PricingTable } from "@/components/PricingTable";
import { ListingsCard } from "@/components/ListingsCard";
import { PriceFlagsCard } from "@/components/PriceFlagsCard";
import { BuyerScript } from "@/components/BuyerScript";
import { SafetyNoticeCard } from "@/components/SafetyNoticeCard";
import { WarrantyDetailsCard } from "@/components/WarrantyDetailsCard";
import { FinancingFeeWarning } from "@/components/FinancingFeeWarning";
import { OdometerNotice } from "@/components/OdometerNotice";
import { ConditionNotice } from "@/components/ConditionNotice";
import { EmailResults } from "@/components/EmailResults";

const VehicleFact = ({ label, value }) => (
  <div className="border-l-2 border-border pl-3">
    <div className="font-mono-plex text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{label}</div>
    <div className="mt-0.5 truncate text-sm font-600 text-foreground">{value || "—"}</div>
  </div>
);

export const ResultsView = ({ result, addonDecisions = {}, onReset }) => {
  const v = result.vehicle || {};
  const disclosures = result.disclosures || [];

  // Split optional add-ons by the buyer's answers in the review step.
  const addons = result.optional_addons || [];
  const requestedAddons = addons.filter((_, i) => addonDecisions[i] === true);
  const declinedAddons = addons.filter((_, i) => addonDecisions[i] !== true);

  // "Flawless" = a perfect score AND not a single question raised by any of our checks.
  const isFlawless =
    result.compliance_score === 100 &&
    !result.odometer_notice &&
    !result.condition_notice &&
    !result.price_mismatch_detected &&
    (result.illegal_addons || []).length === 0 &&
    disclosures.length > 0 &&
    disclosures.every((d) => d.status === "COMPLIANT" || d.status === "NOT APPLICABLE") &&
    (!result.warranty_notice || result.warranty_notice.status === "COMPLIANT") &&
    (!result.safety_notice || result.safety_notice.status === "CONFIRMED");

  return (
    <div data-testid="results-view" className="animate-fade-up space-y-6">
      <div className="space-y-6 bg-background">
      {/* Top bento: score + vehicle */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div
          className="relative flex flex-col items-center justify-center overflow-hidden rounded-xl border border-emerald-500/30 p-6"
          style={{ background: "radial-gradient(circle at 50% 42%, #06251a 0%, #041610 55%, #01100a 100%)", boxShadow: "inset 0 0 40px rgba(0,0,0,0.6)" }}
        >
          <div
            className="pointer-events-none absolute inset-0"
            style={{ background: "repeating-linear-gradient(0deg, rgba(0,0,0,0.22) 0px, rgba(0,0,0,0.22) 1px, transparent 2px, transparent 4px)" }}
            aria-hidden="true"
          />
          <div className="relative mb-3 flex items-center gap-2 font-mono-plex text-sm font-700 uppercase tracking-[0.2em] text-emerald-400">
            <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400 shadow-[0_0_8px_2px_rgba(52,211,153,0.8)]" />
            Compliance Radar
          </div>
          <ScoreGauge score={result.compliance_score} flawless={isFlawless} />
        </div>

        <div className="rounded-xl border border-border bg-card p-6 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Car className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              <h3 className="font-heading text-lg font-800 tracking-tight text-foreground">Vehicle & Dealer</h3>
            </div>
            <span
              className={
                result.condition_notice
                  ? "rounded-full border border-destructive/50 bg-destructive-soft px-2.5 py-1 font-mono-plex text-[10px] font-700 uppercase tracking-[0.12em] text-destructive"
                  : "rounded-full border border-success/30 bg-success-soft px-2.5 py-1 font-mono-plex text-[10px] font-600 uppercase tracking-[0.12em] text-success"
              }
            >
              {result.condition_notice ? "Branded" : "Used"}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <VehicleFact label="Dealer" value={v.dealer_name} />
            <VehicleFact label="Year / Make / Model" value={[v.year, v.make, v.model].filter(Boolean).join(" ")} />
            <VehicleFact label="VIN" value={v.vin} />
            <VehicleFact label="Stock #" value={v.stock_number} />
            <VehicleFact label="Odometer" value={v.odometer_km ? `${v.odometer_km} km` : null} />
          </div>
        </div>
      </div>

      {/* Branded / salvage / total-loss warning — most prominent, shown first */}
      <ConditionNotice notice={result.condition_notice} />

      {/* Advertised Price Check */}
      <ListingsCard result={result} />

      {/* Combined flashing red-flag card: mandatory fees + add-ons the buyer didn't request */}
      <PriceFlagsCard result={result} declinedAddons={declinedAddons} />

      {/* Price Breakdown */}
      <PricingTable result={result} requestedAddons={requestedAddons} />

      {/* Financing fees to review (advisory — does not affect score) */}
      <FinancingFeeWarning warning={result.financing_fee_warning} />

      {/* Safety Standards Certificate notice — shown above the disclosure bubbles */}
      <SafetyNoticeCard notice={result.safety_notice} />

      {/* Warranty terms check — only shows when the dealer is providing a warranty */}
      <WarrantyDetailsCard notice={result.warranty_notice} details={result.warranty_details} />

      {/* Disclosures */}
      <DisclosureList disclosures={disclosures} />

      {/* Odometer rounded-reading notice */}
      <OdometerNotice notice={result.odometer_notice} />

      {/* Buyer script */}
      <BuyerScript script={result.buyer_action_script} />
      </div>
      {/* /report */}

      {/* Email report + reset */}
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 h-full">
          <EmailResults result={result} />
        </div>
        <div
          className="relative flex min-h-[200px] items-center justify-center overflow-hidden rounded-xl border border-emerald-500/20 p-6"
          style={{ background: "radial-gradient(circle at 50% 50%, #063a28 0%, #052617 45%, #020e09 100%)" }}
        >
          {/* rotating radar sweep */}
          <div
            className="animate-radar-spin absolute inset-0"
            style={{ background: "conic-gradient(from 0deg, rgba(52,211,153,0) 0deg, rgba(52,211,153,0.06) 20deg, rgba(52,211,153,0.38) 60deg, rgba(52,211,153,0) 72deg)" }}
            aria-hidden="true"
          />
          {/* range rings + crosshair */}
          <div className="pointer-events-none absolute inset-0" aria-hidden="true">
            <div className="absolute left-1/2 top-1/2 h-44 w-44 -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-400/25" />
            <div className="absolute left-1/2 top-1/2 h-28 w-28 -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-400/20" />
            <div className="absolute left-1/2 top-1/2 h-12 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full border border-emerald-400/20" />
            <div className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-emerald-400/15" />
            <div className="absolute left-0 top-1/2 h-px w-full -translate-y-1/2 bg-emerald-400/15" />
          </div>
          <Button
            data-testid="scan-another-btn"
            variant="outline"
            onClick={onReset}
            className="relative z-10 h-auto w-full rounded-full border-2 border-primary/70 bg-background/70 px-8 py-5 text-base font-700 text-foreground backdrop-blur-sm transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
          >
            <RotateCcw className="mr-2.5 h-5 w-5" aria-hidden="true" /> Scan another contract
          </Button>
        </div>
      </div>
    </div>
  );
};
